import React from "react";
import { ClipboardList, ArrowRight } from "lucide-react";
import { InboxWidget } from "@/components/src/inbox/inbox-widget.component";
import {
	DeliveriesInboxWidget,
	ActiveDelivery,
} from "@/components/src/deliveries-inbox/deliveries-inbox-widget.component";
import {
	UnpaidInvoicesWidget,
	UnpaidInvoice,
} from "@/components/src/unpaid-invoices/unpaid-invoices-widget.component";
import { Tables } from "@/lib/supabase";
import { ProfilesForChat } from "@/lib/types/profile.types";
import { resolveFormColor, contrastText } from "../form-color.utils";

type Channel = Tables<"message_channels">;

interface FormLink {
	id: string;
	title: string;
	bgColor?: string | null;
}

interface VendorDashboardProps {
	vendorId?: string;
	organizationId: string;
	userId: string;
	channels: Channel[];
	getNavigationPath: (channel: Channel) => string;
	getOrgName: (orgId: string) => string | undefined;
	onMarkAsRead?: (channelId: string) => void;
	formList: FormLink[];
	unpaidInvoices: UnpaidInvoice[];
	activeDeliveries: ActiveDelivery[];
	onDeliveryClick: (delivery: ActiveDelivery) => void;
	onMarkDelivered?: (deliveryId: number) => Promise<void>;
	onInvoiceClick: (invoice: UnpaidInvoice) => void;
	onFormClick: (formId: string) => void;
	profilesForChat?: Record<string, ProfilesForChat>;
}

export const VendorDashboard: React.FC<VendorDashboardProps> = ({
	vendorId,
	organizationId,
	userId,
	channels,
	getNavigationPath,
	getOrgName,
	onMarkAsRead,
	formList,
	unpaidInvoices,
	activeDeliveries,
	onDeliveryClick,
	onMarkDelivered,
	onInvoiceClick,
	onFormClick,
	profilesForChat,
}) => {
	return (
		<div className="flex flex-col h-full overflow-hidden">
			{formList.length > 0 && (
				<div
					className="flex items-center gap-2 px-3 py-3 border-b border-gray-200 flex-shrink-0 overflow-x-auto"
					data-testid="vendor-dashboard-form-links"
					aria-label="Formulare Schnellzugriff"
				>
					{formList.map((form) => {
						const resolved = resolveFormColor(form.bgColor);
						const fg = contrastText(resolved.bg);
						return (
							<button
								key={form.id}
								className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium transition-all hover:opacity-85 hover:shadow-md active:scale-[0.97] cursor-pointer shrink-0"
								style={{
									backgroundColor: resolved.bg,
									color: fg,
									border: resolved.border ? `1.5px solid ${resolved.border}` : undefined,
								}}
								onClick={() => onFormClick(form.id)}
								data-testid={`vendor-dashboard-form-${form.id}`}
								aria-label={`Auftragsformular ${form.title} öffnen`}
							>
								<ClipboardList
									className="h-3.5 w-3.5"
									style={{ color: fg }}
								/>
								{form.title}
								<ArrowRight
									className="h-3 w-3 ml-0.5 opacity-60"
									style={{ color: fg }}
								/>
							</button>
						);
					})}
				</div>
			)}

			{formList.length === 0 && (
				<div className="px-3 py-3 border-b border-gray-200 flex-shrink-0">
					<div className="flex items-center gap-2 text-gray-400 text-xs">
						<ClipboardList className="h-4 w-4" />
						<span>Keine Formulare verfügbar</span>
					</div>
				</div>
			)}

			<div className="flex-1 min-h-0 px-3 pb-3 pt-3 grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-1 gap-3">
				<InboxWidget
					channels={channels}
					getNavigationPath={getNavigationPath}
					onMarkAsRead={onMarkAsRead}
					organizationId={organizationId}
					getOrgName={getOrgName}
					userId={userId}
					profilesForChat={profilesForChat}
					className="min-h-0"
					hideSubtitle
				/>

				<div className="min-h-0 flex flex-col gap-3">
					<DeliveriesInboxWidget
						deliveries={activeDeliveries}
						onDeliveryClick={onDeliveryClick}
						onMarkDelivered={onMarkDelivered}
						vendorOrgId={vendorId}
						className="flex-1 min-h-0"
					/>

					<UnpaidInvoicesWidget
						invoices={unpaidInvoices}
						onInvoiceClick={onInvoiceClick}
						vendorOrgId={vendorId}
						className="flex-1 min-h-0"
					/>
				</div>
			</div>
		</div>
	);
};
